/**
 * Dashboard Subscriptions card (#1144): paying subscriptions and free trials from RevenueCat, by
 * platform and by monthly / yearly, and what each of the last 30 days added (#2494). Each number is said once (#2503):
 * a row of what is true now, then the 30 days. The numbers come from the getSubscriptionMetrics callable, which holds
 * the RevenueCat key; this file only asks for them and draws them.
 *
 * How many of those pay the regular price comes from a second callable, getSubscriptionDiscounts (#2538), because
 * RevenueCat's charts cannot answer it. It counts our own records, so its total can differ from RevenueCat's, and it
 * is asked for and drawn beside the rest rather than mixed into it: temporary, and #2543 takes it out in one piece.
 */

const PLATFORMS = [
  { key: "ios", label: "iOS", store: "App Store" },
  { key: "android", label: "Android", store: "Google Play" }
];
const PERIODS = [
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" }
];

// The daily charts (#2494): result key, title, what one bar counts. The title's tooltip says what the chart counts.
const DAILY = [
  { key: "new_customers", label: "New customers", noun: "new customers", about: "First seen by RevenueCat: about the same as installs" },
  { key: "new_trials", label: "Trials started", noun: "trials started", about: "Free trials started" },
  { key: "new_paying", label: "Started paying", noun: "started paying", about: "New paying subscriptions: purchases and trials that converted" },
  // Summed with the 30-day churn rate.
  { key: "churned", label: "Stopped paying", noun: "stopped paying", about: "Paying subscriptions that ended. Churn: those ended over the paying subscriptions on the first day", churn: true }
];

// Tailwind class lists (#1209); whole strings, so Tailwind finds them.
const EMPTY = "px-4 py-8 text-center text-sm text-gray-500 sm:px-6 dark:text-gray-400";
const NUM = "py-2 pl-3 text-right tabular-nums";
const TOTAL = "py-2 pl-3 text-right font-semibold text-gray-900 tabular-nums dark:text-white";
// Today is still counting, so its bar is paler.
const BAR = "w-full rounded-t-[1px] bg-sky-500 dark:bg-sky-400";
const BAR_TODAY = "w-full rounded-t-[1px] bg-sky-500/40 dark:bg-sky-400/40";

// How many paying subscriptions are at the regular price (#2538), and why one could not be placed. A key the
// callable adds later shows as itself rather than as nothing.
const DISCOUNT_ROWS = [
  { key: "full_price", label: "At the regular price" },
  { key: "discounted", label: "Below it" },
  { key: "unknown", label: "Can't be told" }
];
const DISCOUNT_REASONS = {
  below_regular: "pays less than the regular price for its plan and country",
  "waitlist-50-first-year": "bought with the 50% waitlist offer",
  no_record: "the account has no RevenueCat record",
  not_active: "its record says the subscription has ended",
  no_price: "RevenueCat recorded no price",
  no_regular_price: "no regular price for that country and plan",
  other_currency: "paid in another currency than that country's price",
  other_store: "neither the App Store nor Google Play"
};

const panel = {
  deps: null,
  status: "idle", // idle | loading | ok | error
  data: null,
  error: null,
  // getSubscriptionDiscounts, asked for beside the metrics: its failure takes only its own lines, not the card.
  discounts: null,
  discountsError: null
};

let refreshBound = false;

/**
 * Asks getSubscriptionMetrics for the numbers and draws them. Call once the signed-in user is known to
 * be an admin; `refresh` asks the function to skip its cache.
 */
export async function loadSubscriptionMetrics(deps = panel.deps, { refresh = false } = {}) {
  if (!deps?.functions || !deps?.httpsCallable) return;
  panel.deps = deps;
  bindRefresh();
  if (panel.status === "loading") return;
  panel.status = "loading";
  panel.error = null;
  renderSubscriptionsPanel();
  const [metrics, discounts] = await Promise.allSettled([
    deps.httpsCallable(deps.functions, "getSubscriptionMetrics")({ refresh }),
    deps.httpsCallable(deps.functions, "getSubscriptionDiscounts")({ refresh })
  ]);
  if (metrics.status === "fulfilled") {
    panel.data = metrics.value.data;
    panel.status = "ok";
  } else {
    console.error("❌ getSubscriptionMetrics failed:", metrics.reason);
    panel.error = metrics.reason?.message || String(metrics.reason);
    panel.status = "error";
  }
  if (discounts.status === "fulfilled") {
    panel.discounts = discounts.value.data;
    panel.discountsError = null;
  } else {
    console.error("❌ getSubscriptionDiscounts failed:", discounts.reason);
    // The last answer goes with it: an old count drawn as the current one is worse than none.
    panel.discounts = null;
    panel.discountsError = discounts.reason?.message || String(discounts.reason);
  }
  renderSubscriptionsPanel();
}

/** Draws the current state into the dashboard template, if it is mounted. */
export function renderSubscriptionsPanel() {
  const body = document.getElementById("dashSubscriptions");
  const detail = document.getElementById("dashSubscriptionsDetail");
  const meta = document.getElementById("dashSubscriptionsMeta");
  const button = document.getElementById("dashSubscriptionsRefresh");
  if (!body) return;
  if (button) button.disabled = panel.status === "loading" || !panel.deps;
  if (meta) meta.textContent = metaText(panel);

  if (panel.status === "error") {
    body.innerHTML = `<p class="px-4 py-8 text-center text-sm text-red-700 sm:px-6 dark:text-red-400">Could not load subscriptions from RevenueCat: ${escapeHtml(panel.error)}</p>`;
  } else if (panel.data) {
    body.innerHTML = subscriptionsSummaryHtml(panel.data, discountsState());
  } else {
    body.innerHTML = `<p class="${EMPTY}">Loading subscriptions from RevenueCat…</p>`;
  }
  if (detail) {
    detail.innerHTML = panel.data && panel.status !== "error"
      ? subscriptionsDetailHtml(panel.data, discountsState())
      : `<p class="${EMPTY}">${panel.status === "error" ? "Nothing to show until RevenueCat answers." : "Loading…"}</p>`;
  }
}

/** What the render functions are told about the regular-price count: the answer, an error, or null while it is coming. */
function discountsState() {
  return panel.discounts || (panel.discountsError ? { error: panel.discountsError } : null);
}

function bindRefresh() {
  if (refreshBound) return;
  refreshBound = true;
  document.addEventListener("click", (e) => {
    if (e.target.closest("#dashSubscriptionsRefresh")) loadSubscriptionMetrics(panel.deps, { refresh: true });
  });
}

function metaText({ status, data }) {
  if (status === "loading" && data) return "Refreshing from RevenueCat…";
  if (!data) return "Live from RevenueCat.";
  const when = formatTime(data.fetched_at);
  return `From RevenueCat, as of ${when}${data.cached ? " (cached)" : ""}.`;
}

function formatTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "an unknown time";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatMoney(metric, currency) {
  if (!metric || typeof metric.value !== "number") return "–";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(metric.value);
  } catch (err) {
    return String(Math.round(metric.value));
  }
}

function cellCount(value) {
  return typeof value === "number" ? value : 0;
}

/** Row, column and grand totals of a `{ ios: { monthly, yearly }, android: {…} }` grid. */
export function gridTotals(grid = {}) {
  const rows = Object.fromEntries(PLATFORMS.map((p) => [p.key, PERIODS.reduce((sum, d) => sum + cellCount(grid[p.key]?.[d.key]), 0)]));
  const cols = Object.fromEntries(PERIODS.map((d) => [d.key, PLATFORMS.reduce((sum, p) => sum + cellCount(grid[p.key]?.[d.key]), 0)]));
  const total = Object.values(rows).reduce((a, b) => a + b, 0);
  return { rows, cols, total };
}

function gridTableHtml(title, grid) {
  const { rows, cols, total } = gridTotals(grid);
  const body = PLATFORMS.map((p) => `
      <tr>
        <th scope="row" class="py-2 text-left font-medium text-gray-900 dark:text-white">${p.label} <span class="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">${p.store}</span></th>
        ${PERIODS.map((d) => `<td class="${NUM} text-gray-700 dark:text-gray-300">${cellCount(grid?.[p.key]?.[d.key])}</td>`).join("")}
        <td class="${TOTAL}">${rows[p.key]}</td>
      </tr>`).join("");
  return `
    <table class="w-full text-sm">
      <caption class="pb-2 text-left text-sm font-semibold text-gray-900 dark:text-white">${escapeHtml(title)}</caption>
      <thead class="border-b border-gray-200 dark:border-white/10"><tr><th scope="col" class="py-2 text-left"><span class="sr-only">Platform</span></th>${PERIODS.map((d) => `<th scope="col" class="${NUM} text-xs font-semibold text-gray-500 dark:text-gray-400">${d.label}</th>`).join("")}<th scope="col" class="${NUM} text-xs font-semibold text-gray-500 dark:text-gray-400">Total</th></tr></thead>
      <tbody class="divide-y divide-gray-100 dark:divide-white/5">${body}</tbody>
      <tfoot class="border-t border-gray-300 dark:border-white/20"><tr><th scope="row" class="py-2 text-left font-semibold text-gray-900 dark:text-white">Total</th>${PERIODS.map((d) => `<td class="${TOTAL}">${cols[d.key]}</td>`).join("")}<td class="${TOTAL}">${total}</td></tr></tfoot>
    </table>`;
}

/** The headline numbers, always shown (#1623). `discounts` is a getSubscriptionDiscounts answer (#2538), or null. */
export function subscriptionsSummaryHtml(data, discounts = null) {
  const paying = gridTotals(data.paying);
  const trials = gridTotals(data.trials);
  const overview = data.overview || {};
  const yearlyShare = paying.total ? `${Math.round((paying.cols.yearly / paying.total) * 100)}% yearly` : null;

  const stats = [
    { value: paying.total, label: "Paying subscriptions", subs: [fullPriceText(discounts), setToCancelText(data.set_to_cancel?.paying, "set to cancel"), yearlyShare] },
    { value: trials.total, label: "In free trial", subs: [setToCancelText(data.set_to_cancel?.trials, "cancelled, won't convert")] },
    { value: formatMoney(overview.mrr, data.currency), label: "MRR", subs: [] },
    { value: formatMoney(overview.revenue, data.currency), label: "Revenue", subs: [{ text: "last 28 days" }] }
  ];

  return `
    <dl class="grid grid-cols-2 gap-x-6 gap-y-5 px-4 py-5 sm:grid-cols-4 sm:px-6">${stats.map((s) => `
      <div>
        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">${escapeHtml(s.label)}</dt>
        <dd class="mt-1 text-2xl font-semibold tracking-tight text-gray-900 tabular-nums dark:text-white">${escapeHtml(String(s.value))}</dd>${subLineHtml(s.subs)}
      </div>`).join("")}
    </dl>
    ${subscriptionsDailyHtml(data)}`;
}

/**
 * The line under a count of running trials or paying subscriptions saying how many are set to cancel (#2503): amber
 * when there are any, "–" with RevenueCat's answer in the tooltip when it couldn't be read.
 */
function setToCancelText(status, words) {
  if (!status) return null;
  if (status.error) return { text: `– ${words}`, tooltip: status.error };
  return { text: `${status.count} ${words}`, warn: status.count > 0 };
}

/**
 * The line under the paying subscriptions saying how many are at the regular price (#2538). Its own total, from our
 * own records, so the line reads straight even where RevenueCat counts something else; the tooltip says so, and the
 * table under "By store and plan" has the rest. "–" with the reason when the count could not be read.
 */
function fullPriceText(discounts) {
  if (!discounts) return null;
  if (discounts.error) return { text: "– at full price", tooltip: discounts.error };
  const { full_price: full, paying, discounted, unknown } = discounts;
  if (!paying) return { text: "0 at full price", tooltip: "Our own records hold no paying subscription." };
  const parts = [`${discounted} below it`];
  if (unknown) parts.push(`${unknown} that can't be told`);
  return {
    text: `${full} of ${paying} at full price`,
    tooltip: `Counted from our own records of each account's last RevenueCat event, so the total can differ from RevenueCat's own above: ${parts.join(", ")}.`
  };
}

function subLineHtml(subs) {
  const parts = subs.filter(Boolean).map((sub) => (typeof sub === "string" ? { text: sub } : sub));
  if (!parts.length) return "";
  const html = parts.map((p) => {
    const tone = p.warn ? "font-medium text-amber-700 dark:text-amber-400" : "";
    const tip = p.tooltip ? ` data-tooltip="${escapeHtml(p.tooltip)}"` : "";
    return tone || tip ? `<span class="${tone}"${tip}>${escapeHtml(p.text)}</span>` : escapeHtml(p.text);
  }).join(" · ");
  return `
        <dd class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">${html}</dd>`;
}

function shortDate(iso) {
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function percent(rate) {
  return new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(rate);
}

/** The figure beside a chart's title: its 30-day total, and for the ended chart the churn rate (#2494). */
function chartFigure(chart, total, data) {
  if (!chart.churn) return String(total);
  const rate = data?.churn?.rate;
  return typeof rate === "number" ? `${total} · ${percent(rate)} churn` : String(total);
}

function chartTitleHtml(chart, figure) {
  return `
      <div class="flex items-baseline justify-between gap-3">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300" data-tooltip="${escapeHtml(chart.about)}">${escapeHtml(chart.label)}</h4>
        <span class="text-sm font-semibold text-gray-900 tabular-nums dark:text-white">${escapeHtml(figure)}</span>
      </div>`;
}

function dailyChartHtml(chart, days, data) {
  if (chart.churn && data?.churn?.error) {
    return `
    <div data-daily-chart="${chart.key}">${chartTitleHtml(chart, "–")}
      <p class="mt-2 text-xs text-red-700 dark:text-red-400">${escapeHtml(data.churn.error)}</p>
    </div>`;
  }
  const counts = days.map((d) => cellCount(d[chart.key]));
  const max = Math.max(...counts, 0);
  const total = counts.reduce((a, b) => a + b, 0);
  const bars = days.map((d, i) => {
    const n = counts[i];
    const height = max ? Math.max(n > 0 ? 3 : 0, (n / max) * 100) : 0;
    const text = `${shortDate(d.date)}: ${n} ${chart.noun}${d.today ? " (today, still counting)" : ""}`;
    return `<li class="flex h-full flex-1 items-end" data-daily-day="${escapeHtml(d.date)}" data-tooltip="${escapeHtml(text)}"><span class="sr-only">${escapeHtml(text)}</span><div aria-hidden="true" class="${d.today ? BAR_TODAY : BAR}" style="height: ${height}%"></div></li>`;
  }).join("");
  return `
    <div data-daily-chart="${chart.key}">${chartTitleHtml(chart, chartFigure(chart, total, data))}
      <ul class="mt-2 flex h-16 items-end gap-px border-b border-gray-200 dark:border-white/10">${bars}</ul>
      <div class="mt-1 flex justify-between text-[11px]/4 text-gray-500 dark:text-gray-400"><span>${escapeHtml(shortDate(days[0].date))}</span><span>${escapeHtml(shortDate(days[days.length - 1].date))}</span></div>
    </div>`;
}

/** What each of the last 30 days added (#2494); nothing when the function sent no history. */
export function subscriptionsDailyHtml(data) {
  const days = Array.isArray(data?.daily) ? data.daily : [];
  if (!days.length) return "";
  return `
    <section class="border-t border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Last ${days.length} days</h3>
      <div class="mt-3 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-4">${DAILY.map((chart) => dailyChartHtml(chart, days, data)).join("")}</div>
    </section>`;
}

/**
 * What the paying subscriptions pay, behind "By store and plan" (#2538): the three counts, why each one that could
 * not be placed was not, and the two things the count cannot see. Nothing while it is still coming.
 */
function trialsLeftOut(trials) {
  if (!trials) return "";
  return trials === 1
    ? " The one running free trial pays nothing yet and is left out."
    : ` The ${trials} running free trials pay nothing yet and are left out.`;
}

export function subscriptionsDiscountsHtml(discounts) {
  if (!discounts) return "";
  if (discounts.error) {
    return `
    <section class="border-t border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">What they pay</h3>
      <p class="mt-2 text-sm text-red-700 dark:text-red-400">Could not count what the paying subscriptions pay: ${escapeHtml(discounts.error)}</p>
    </section>`;
  }

  const rows = DISCOUNT_ROWS.map((row) => `
      <tr>
        <th scope="row" class="py-2 text-left font-medium text-gray-900 dark:text-white">${escapeHtml(row.label)}</th>
        <td class="${NUM} text-gray-700 dark:text-gray-300">${cellCount(discounts[row.key])}</td>
        <td class="py-2 pl-3 text-right text-xs text-gray-500 dark:text-gray-400">${discounts.paying ? `${Math.round((cellCount(discounts[row.key]) / discounts.paying) * 100)}%` : "–"}</td>
      </tr>`).join("");
  const reasons = Object.entries(discounts.reasons || {}).sort((a, b) => b[1] - a[1]);
  const reasonsHtml = reasons.length
    ? `<ul class="mt-3 space-y-1 text-xs text-gray-500 dark:text-gray-400">${reasons.map(([key, count]) => `
        <li><span class="font-medium text-gray-700 tabular-nums dark:text-gray-300">${count}</span> ${escapeHtml(DISCOUNT_REASONS[key] || key)}</li>`).join("")}</ul>`
    : "";

  return `
    <section class="border-t border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">What they pay</h3>
      <table class="mt-3 w-full max-w-md text-sm">
        <caption class="sr-only">The paying subscriptions by what they pay</caption>
        <tbody class="divide-y divide-gray-100 dark:divide-white/5">${rows}</tbody>
        <tfoot class="border-t border-gray-300 dark:border-white/20"><tr><th scope="row" class="py-2 text-left font-semibold text-gray-900 dark:text-white">Paying subscriptions</th><td class="${TOTAL}">${cellCount(discounts.paying)}</td><td></td></tr></tfoot>
      </table>${reasonsHtml}
      <p class="mt-3 max-w-prose text-xs text-gray-500 dark:text-gray-400">
        From our own records of each account's last RevenueCat event, not from RevenueCat's charts, which cannot be
        asked about an offer or a price: the total above can therefore differ from RevenueCat's. A price below the
        <em>current</em> regular price for that plan and country reads as a discount, so a price rise would make
        subscribers on the old price look discounted.${trialsLeftOut(discounts.trials)}${discounts.capped ? ` Counted over the first ${discounts.max_accounts} accounts only.` : ""}
      </p>
    </section>`;
}

/** The store × plan tables, behind "By store and plan", and what they pay (#2538). */
export function subscriptionsDetailHtml(data, discounts = null) {
  const paying = gridTotals(data.paying);
  const trials = gridTotals(data.trials);
  const overview = data.overview || {};

  // RevenueCat's own totals also count subscriptions outside the four cells (another store or duration).
  const notes = [];
  const otherPaying = cellCount(overview.active_subscriptions?.value) - paying.total;
  const otherTrials = cellCount(overview.active_trials?.value) - trials.total;
  if (otherPaying > 0) notes.push(`${otherPaying} paying`);
  if (otherTrials > 0) notes.push(`${otherTrials} in trial`);
  const note = notes.length
    ? `<p class="px-4 pb-5 text-xs text-gray-500 sm:px-6 dark:text-gray-400">RevenueCat also counts ${notes.join(" and ")} outside these tables: another store, or a duration other than monthly or yearly.</p>`
    : "";

  return `
    <div class="grid grid-cols-1 gap-x-10 gap-y-6 px-4 py-5 sm:px-6 lg:grid-cols-2">
      ${gridTableHtml("Paying subscriptions", data.paying)}
      ${gridTableHtml("Free trials", data.trials)}
    </div>
    ${note}
    ${subscriptionsDiscountsHtml(discounts)}`;
}

/** Both parts, as one card body. */
export function subscriptionsHtml(data, discounts = null) {
  return subscriptionsSummaryHtml(data, discounts) + subscriptionsDetailHtml(data, discounts);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
